import type { Content, TableLayout, TableCell } from 'pdfmake/interfaces';
import type { ViewBlock, ViewSection } from '@/data/schemas';
import { cm, pdfColors, tableLayouts } from '../theme.pdf';
import { blockToContentForm } from './blockRenderers';
import { BLOCK_SPACING, SECTION_SPACING } from './layout';

const COMMENT_MEDIA_COLUMNS = 3;
const COMMENT_MEDIA_MAX_WIDTH_CM = 4.5;
const COMMENT_MEDIA_MAX_HEIGHT_CM = 3.2;

function canRenderImage(src?: string): src is string {
  if (!src) return false;
  return src.startsWith('data:') || src.startsWith('blob:');
}

function buildCommentMediaGrid(media: ViewSection['commentMedia']): Content {
  const items = media ?? [];
  const fitWidth = cm(COMMENT_MEDIA_MAX_WIDTH_CM);
  const fitHeight = cm(COMMENT_MEDIA_MAX_HEIGHT_CM);
  const rows: TableCell[][] = [];

  for (let i = 0; i < items.length; i += COMMENT_MEDIA_COLUMNS) {
    const rowItems = items.slice(i, i + COMMENT_MEDIA_COLUMNS);
    const row: TableCell[] = rowItems.map((entry) => {
      if (!canRenderImage(entry.dataUrl)) {
        return {
          text: 'Foto non disponibile',
          style: 'bodyMuted',
          alignment: 'center',
          valign: 'middle',
          fillColor: pdfColors.lightGray,
        } as TableCell;
      }

      return {
        image: entry.dataUrl,
        fit: [fitWidth, fitHeight],
        alignment: 'center',
        valign: 'middle',
      } as TableCell;
    });

    while (row.length < COMMENT_MEDIA_COLUMNS) {
      row.push({ text: '' });
    }

    rows.push(row);
  }

  return {
    table: {
      widths: Array.from({ length: COMMENT_MEDIA_COLUMNS }, () => '*'),
      body: rows,
    },
    layout: tableLayouts.default as TableLayout,
    margin: [0, 0, 0, BLOCK_SPACING],
  };
}

export function sectionToContent(section: ViewSection, numbering?: string): Content {
  const title = numbering ? `${numbering} ${section.title}` : section.title;
  const stack: Content[] = [];

  if (section.description) {
    stack.push({
      text: section.description,
      style: 'sectionDescription',
    });
  }

  section.blocks.forEach((block) => {
    stack.push(blockToContentForm(block as ViewBlock));
  });

  const hasComment = Boolean(section.comment);
  const hasCommentMedia = (section.commentMedia?.length ?? 0) > 0;

  if (hasComment || hasCommentMedia) {
    stack.push({
      text: 'Note aggiuntive',
      style: 'sectionCommentLabel',
      margin: [0, BLOCK_SPACING * 1.25, 0, BLOCK_SPACING / 2],
    });
    if (section.comment) {
      stack.push({
        text: section.comment,
        style: 'sectionCommentText',
        margin: [0, 0, 0, BLOCK_SPACING / 2],
      });
    }

    if (hasCommentMedia) {
      stack.push(buildCommentMediaGrid(section.commentMedia));
    }
  }

  return {
    table: {
      widths: ['*'],
      dontBreakRows: false,
      keepWithHeaderRows: 1,
      body: [
        [
          {
            text: title,
            style: 'formBoxTitle',
            fillColor: pdfColors.lightGray,
          },
        ],
        [
          {
            stack,
            margin: [0, 0, 0, 0],
          },
        ],
      ],
    },
    layout: tableLayouts.formOuter as TableLayout,
    margin: [0, SECTION_SPACING, 0, 0],
  };
}
